// Reihenfolge der Bildschirme und der geführten Fotoaufnahme.
//
// Fotoaufnahme (Vorgabe): an der Tür beginnen, Flur abschnittsweise, dann
// ringsum Raum für Raum, große Schränke zusätzlich geöffnet. Nebenräume
// (Keller, Dachboden, Garage, Balkon) zuletzt.
//
// Seit 08.10.2026 (Grundriss): Eingang und Flur zuerst (Schritt "eingang"),
// die KI erkennt die Türen, der Kunde ordnet ihnen Räume zu ("grundriss").
// Die Räume folgen dann Etage für Etage den Türen. "raeume" (Räume zählen)
// bleibt der Weg für Wohnungen ohne Flur und für ältere Aufnahmen.

import type { Problem, RoomType } from "@umzugsscan/shared";
import type { Draft, Nav, Room, Screen } from "./zustand.ts";

// Seit 09.10.2026: Fotos zuerst, Formulare danach. Wer 15 Minuten in die Fotos investiert hat, bricht
// bei Kontakt und Adressen seltener ab. Kontakt und Termin stehen gebündelt auf einer Seite ("kontakt");
// "termin" gibt es nur noch für ältere gespeicherte Stände (zählt wie "kontakt").
export const FLOW: Screen[] = [
  // 30.10.2026: erst den Flur zeichnen (Form, Türen), dann die Flurfotos – ohne KI für die Türen.
  "willkommen",
  "grundriss",
  "eingang",
  "fotos",
  // 30.10.2026: Sonderteile, Zerbrechliches und Markieren auf einer Seite, Lampen bei den Leistungen.
  "besonderes",
  "kueche",
  "leistungen",
  "kontakt",
  "auszug",
  "einzug",
  "pruefung",
];

/** Anteil am Fortschrittsbalken: Die Fotos sind der längste Teil, Formulare gehen schnell. */
const WEIGHT: Partial<Record<Screen, number>> = {
  willkommen: 0.5, eingang: 1, grundriss: 1, fotos: 6, besonderes: 1, kueche: 1, leistungen: 1, kontakt: 1.5, auszug: 1.5, einzug: 1.5,
  pruefung: 1,
};
const weightOf = (s: Screen) => WEIGHT[s] ?? 1;

/** Ältere gespeicherte Stände und Unterseiten zählen wie ihr Schritt im Ablauf. */
const ALIAS: Partial<Record<Screen, Screen>> = {
  termin: "kontakt",
  markieren: "besonderes",
  sonderteile: "besonderes",
  zerbrechliches: "besonderes",
  lampen: "leistungen",
};
const canonical = (s: Screen): Screen => ALIAS[s] ?? s;

/** Schritte, die nur unter Bedingungen kommen (Küche nur mit Küche in der Wohnung). */
function skipped(s: Screen, rooms?: Room[]): boolean {
  return s === "kueche" && rooms !== undefined && !rooms.some((r) => r.type === "kueche");
}

export function nextScreen(s: Screen, rooms?: Room[]): Screen {
  // "raeume" (Räume zählen) ist nur der Weg für Wohnungen ohne Flur.
  if (s === "raeume") return "fotos";
  const i = FLOW.indexOf(canonical(s));
  const next = i < 0 || i === FLOW.length - 1 ? "pruefung" : FLOW[i + 1]!;
  return skipped(next, rooms) ? nextScreen(next, rooms) : next;
}

export function prevScreen(s: Screen, rooms?: Room[]): Screen {
  if (s === "raeume") return "grundriss";
  const i = FLOW.indexOf(canonical(s));
  const prev = i <= 0 ? "willkommen" : FLOW[i - 1]!;
  return skipped(prev, rooms) ? prevScreen(prev, rooms) : prev;
}

/** Fortschritt 0..1 für den Balken oben. */
export function progress(nav: Nav, guideCount: number): number {
  const screen = nav.screen === "raeume" ? "grundriss" : canonical(nav.screen);
  const i = FLOW.indexOf(screen);
  if (i < 0) return 1;
  const total = FLOW.reduce((sum, f) => sum + weightOf(f), 0);
  const before = FLOW.slice(0, i).reduce((sum, f) => sum + weightOf(f), 0);
  // Der Foto-Bildschirm zählt anteilig über die Räume.
  const part = screen === "fotos" && guideCount > 0 ? (Math.min(nav.guide, guideCount) / guideCount) * weightOf("fotos") : 0;
  return (before + part) / total;
}

export type GuideStep =
  | { kind: "tuer"; roomId: string }
  | { kind: "flur"; roomId: string }
  | { kind: "raum"; roomId: string };

/** Etagen in Laufreihenfolge: Eingangsebene, dann aufwärts, dann Keller (kein Hoch und Runter). */
export function floorRank(f: number): number {
  return f >= 0 ? f : 100 - f;
}

/** Flur der Eingangsebene (an ihm hängen die Türen des Grundrisses). */
export function entranceHall(rooms: Room[]): Room | undefined {
  return rooms.filter((r) => r.type === "flur" && (r.floor ?? 0) === 0).sort((a, b) => a.sort - b.sort)[0];
}

/** Reihenfolge der Räume im Grundriss: Etage, Flur zuerst, Türen, Räume ohne Flurtür. */
export function floorPlanKey(r: Room): number[] {
  return [floorRank(r.floor ?? 0), r.type === "flur" ? 0 : r.hallOrder != null ? 1 : 2, r.hallOrder ?? 0, r.sort];
}
export function byFloorPlan(a: Room, b: Room): number {
  const ka = floorPlanKey(a), kb = floorPlanKey(b);
  for (let i = 0; i < ka.length; i++) if (ka[i]! !== kb[i]!) return ka[i]! - kb[i]!;
  return 0;
}

/** Ist die Wohnung über den Grundriss (Flur und Türen) erfasst? */
export const hasFloorPlan = (rooms: Room[]) => rooms.some((r) => r.hallOrder != null);

export function guideSteps(rooms: Room[]): GuideStep[] {
  if (rooms.length === 0) return [];
  if (hasFloorPlan(rooms)) return floorPlanSteps(rooms);
  const sorted = [...rooms].sort((a, b) => a.sort - b.sort);
  const halls = sorted.filter((r) => r.type === "flur");
  const steps: GuideStep[] = [{ kind: "tuer", roomId: (halls[0] ?? sorted[0]!).id }];
  for (const h of halls) steps.push({ kind: "flur", roomId: h.id });
  for (const r of sorted) if (r.type !== "flur") steps.push({ kind: "raum", roomId: r.id });
  return steps;
}

/**
 * Grundriss-Reihenfolge (08.10.2026): Etage für Etage, im Flur den Türen nach
 * (hallOrder), danach Räume ohne Flurtür (Balkon, Keller …). Der Flur der
 * Eingangsebene ist schon im Schritt "eingang" fotografiert; Flure weiterer
 * Etagen kommen am Anfang ihrer Etage.
 */
function floorPlanSteps(rooms: Room[]): GuideStep[] {
  const hall = entranceHall(rooms);
  const rest = withChildren(rooms.filter((r) => r.id !== hall?.id).sort(byFloorPlan), rooms);
  return rest.map((r) => (r.type === "flur" ? { kind: "flur", roomId: r.id } : { kind: "raum", roomId: r.id }));
}

/**
 * Räume hinter einem Zimmer (15.10.2026, z. B. Balkon hinter dem Wohnzimmer: hallRoomId zeigt auf ein Zimmer, nicht
 * auf einen Flur) kommen direkt nach ihrem Zimmer, auch verschachtelt.
 */
export function withChildren(sorted: Room[], all: Room[]): Room[] {
  const isChild = (r: Room) => {
    const p = r.hallRoomId ? all.find((x) => x.id === r.hallRoomId) : undefined;
    return !!p && p.type !== "flur";
  };
  const out: Room[] = [];
  const seen = new Set<string>();
  const add = (r: Room) => {
    if (seen.has(r.id)) return;
    seen.add(r.id);
    out.push(r);
    for (const c of sorted.filter((x) => x.hallRoomId === r.id && isChild(x))) add(c);
  };
  for (const r of sorted) if (!isChild(r) || !sorted.some((x) => x.id === r.hallRoomId)) add(r);
  return out;
}

export const GUIDE_TEXT = {
  tuer: {
    title: "Beginnen Sie an der Wohnungstür",
    text: "Stellen Sie sich mit dem Rücken zur Wohnungstür und fotografieren Sie den Eingangsbereich. Ein Foto genügt.",
  },
  flur: {
    title: "Flur Stück für Stück",
    text: "Gehen Sie den Flur ab und machen Sie alle 2–3 Meter ein Foto in Gehrichtung. Garderobe, Schuhschrank und Regale sollen ganz zu sehen sein.",
  },
  rundum: {
    title: "Einmal ringsum",
    text: "Stellen Sie sich in die Tür und machen Sie ein Übersichtsfoto. Danach jede Wand einzeln, sodass alle Möbel vollständig im Bild sind. Licht einschalten hilft.",
  },
  schrank: {
    title: "Große Schränke geöffnet",
    text: "Öffnen Sie große Schränke (Kleiderschrank, Schrankwand, Einbauschrank) und fotografieren Sie den Inhalt. So können wir die Zahl der Kartons einschätzen.",
  },
} as const;

/** Zusätzlicher Hinweis je Raumtyp unter der Anweisung. */
export const ROOM_HINT: Partial<Record<RoomType, string>> = {
  kueche: "Bitte auch Oberschränke, Arbeitsfläche und Geräte. Ober- und Unterschränke kurz öffnen.",
  bad: "Schränke, Regale und Waschmaschine/Trockner, falls vorhanden.",
  wohnen: "Schrankwand, Sofa, Regale – bitte auch Bilder und Lampen.",
  arbeit: "Schreibtisch, Regale und Aktenschränke.",
  keller: "Regale, gelagerte Kisten und alles, was mit soll.",
  dachboden: "Gelagerte Kisten und Möbel; Zugang (Treppe/Luke) bitte mit fotografieren.",
  garage: "Regale, Fahrräder, Werkzeug, Reifen.",
  balkon: "Möbel, Pflanzen, Grill.",
};

/** Wohin "Beheben" in der Prüfung springt. */
export function navForProblem(p: Problem, d: Draft): Partial<Nav> {
  const back = { returnToCheck: true };
  switch (p.target.kind) {
    case "einwilligung":
      return { ...back, screen: "willkommen" };
    case "lampen":
      return { ...back, screen: "leistungen" };
    case "kontakt":
      return { ...back, screen: "kontakt" };
    case "termin":
      return { ...back, screen: "kontakt" };
    case "adresse":
      return { ...back, screen: p.target.id === "to" ? "einzug" : "auszug" };
    case "raeume":
      if (hasFloorPlan(d.rooms)) return { ...back, screen: p.code === "tuer_fehlt" ? "eingang" : "grundriss" };
      if (p.code === "tuer_fehlt") return { ...back, screen: "fotos", guide: 0, phase: "rundum" };
      return { ...back, screen: "raeume" };
    case "raum":
    case "foto": {
      const roomId =
        p.target.kind === "raum" ? p.target.id : d.photos.find((x) => x.id === p.target.id)?.roomId;
      const steps = guideSteps(d.rooms);
      const photo = p.target.kind === "foto" ? d.photos.find((x) => x.id === p.target.id) : undefined;
      // Flur der Eingangsebene: Fotos im Schritt "eingang".
      if (hasFloorPlan(d.rooms) && roomId === entranceHall(d.rooms)?.id) return { ...back, screen: "eingang" };
      let i = steps.findIndex((s) => s.roomId === roomId && s.kind !== "tuer");
      if (photo?.step === "tuer") i = 0;
      const phase = p.code === "schrank_zu" || p.code === "ki_schrank_zu" || photo?.step === "schrank_offen" ? "schrank" : "rundum";
      return { ...back, screen: "fotos", guide: Math.max(0, i), phase };
    }
  }
}
